<template>
  <div>
    <b-card>
      <template #header>
        <div class="d-flex justify-content-between align-items-center">
          <h5 class="mb-0">Gestión de Reglas de Impuestos</h5>
          <b-button variant="primary" size="sm" @click="showCreateModal">
            <b-icon icon="plus"></b-icon> Nueva Regla
          </b-button>
        </div>
      </template>

      <!-- Filters -->
      <div class="mb-3">
        <b-row>
          <b-col md="3">
            <b-form-group label="Filtrar por Categoría:" size="sm">
              <b-form-select v-model="filters.supply_category_id" :options="supplyCategoryOptions" size="sm" @change="loadTaxRules">
                <template #first>
                  <b-form-select-option :value="null">Todas las categorías</b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Filtrar por Producto:" size="sm">
              <b-form-select v-model="filters.supply_id" :options="supplyOptions" size="sm" @change="loadTaxRules">
                <template #first>
                  <b-form-select-option :value="null">Todos los productos</b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Filtrar por Tipo de Impuesto:" size="sm">
              <b-form-select v-model="filters.tax_code" :options="taxCodeFilterOptions" size="sm" @change="loadTaxRules">
                <template #first>
                  <b-form-select-option :value="null">Todos los tipos</b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Estado:" size="sm">
              <b-form-select v-model="filters.is_active" size="sm" @change="loadTaxRules">
                <b-form-select-option :value="null">Todos</b-form-select-option>
                <b-form-select-option :value="true">Activos</b-form-select-option>
                <b-form-select-option :value="false">Inactivos</b-form-select-option>
              </b-form-select>
            </b-form-group>
          </b-col>
        </b-row>
      </div>

      <!-- Tax Rules Table -->
      <b-table 
        :items="taxRules" 
        :fields="ruleFields"
        striped hover responsive small
        show-empty
        empty-text="No hay reglas de impuestos configuradas">
        
        <template #cell(scope)="row">
          <div>
            <b-badge v-if="row.item.supply_category" variant="info">
              Categoría: {{ row.item.supply_category.name }}
            </b-badge>
            <b-badge v-else-if="row.item.supply" variant="success">
              Producto: {{ row.item.supply.name }}
            </b-badge>
            <b-badge v-else variant="warning">Sin Asignar</b-badge>
          </div>
        </template>
        
        <template #cell(tax_info)="row">
          <div>
            <b-badge :variant="getTaxBadgeVariant(row.item.tax_catalog?.tax_code)">
              {{ row.item.tax_catalog?.name || 'N/A' }}
            </b-badge>
            <br>
            <small class="text-muted">
              {{ row.item.tax_catalog_line?.rate_name }} - {{ row.item.tax_catalog_line?.rate_value }}%
            </small>
          </div>
        </template>
        
        <template #cell(priority)="row">
          <b-badge :variant="getPriorityVariant(row.item.priority)">
            {{ row.item.priority }}
          </b-badge>
        </template>
        
        <template #cell(effective_dates)="row">
          <div>
            <small class="text-muted">
              Desde: {{ formatDate(row.item.effective_from) }}<br>
              Hasta: {{ formatDate(row.item.effective_to) || 'Sin límite' }}
            </small>
          </div>
        </template>
        
        <template #cell(flags)="row">
          <div>
            <b-badge v-if="row.item.is_default" variant="primary">Por Defecto</b-badge>
            <b-badge :variant="row.item.is_active ? 'success' : 'secondary'">
              {{ row.item.is_active ? 'Activo' : 'Inactivo' }}
            </b-badge>
          </div>
        </template>
        
        <template #cell(actions)="row">
          <b-button-group size="sm">
            <b-button variant="info" @click="editRule(row.item)" title="Editar">
              <b-icon icon="pencil"></b-icon>
            </b-button>
            <b-button variant="warning" @click="duplicateRule(row.item)" title="Duplicar">
              <b-icon icon="files"></b-icon>
            </b-button>
            <b-button variant="danger" @click="deleteRule(row.item.id)" title="Eliminar">
              <b-icon icon="trash"></b-icon>
            </b-button>
          </b-button-group>
        </template>
      </b-table>
    </b-card>

    <!-- Create/Edit Rule Modal -->
    <b-modal 
      id="modal-rule-form" 
      v-model="showRuleModal" 
      :title="ruleForm.id ? 'Editar Regla de Impuesto' : 'Crear Regla de Impuesto'"
      @hidden="resetRuleForm"
      size="xl">
      
      <b-form>
        <!-- Scope Selection -->
        <b-card class="mb-3">
          <template #header>
            <h6 class="mb-0">Alcance de la Regla</h6>
          </template>
          
          <b-form-group>
            <b-form-radio-group v-model="ruleScope" :options="scopeOptions" stacked @change="onScopeChange"></b-form-radio-group>
          </b-form-group>
          
          <div v-if="ruleScope === 'category'">
            <b-form-group label="Categoría de Producto:" required>
              <b-form-select v-model="ruleForm.supply_category_id" :options="supplyCategoryOptions" required>
                <template #first>
                  <b-form-select-option :value="null" disabled>Selecciona una categoría</b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>
          </div>
          
          <div v-if="ruleScope === 'product'">
            <b-form-group label="Producto Específico:" required>
              <vu-select 
                v-model="ruleForm.supply_id" 
                :options="supplies" 
                :reduce="(x) => x.id"
                label="name"
                placeholder="Buscar producto..."
                :searchable="true">
              </vu-select>
            </b-form-group>
          </div>
        </b-card>

        <!-- Tax Configuration -->
        <b-card class="mb-3">
          <template #header>
            <h6 class="mb-0">Configuración de Impuesto</h6>
          </template>
          
          <b-row>
            <b-col md="6">
              <b-form-group label="Catálogo de Impuestos:" required>
                <b-form-select v-model="ruleForm.tax_catalog_id" :options="taxCatalogOptions" @change="onTaxCatalogChange" required>
                  <template #first>
                    <b-form-select-option :value="null" disabled>Selecciona un catálogo</b-form-select-option>
                  </template>
                </b-form-select>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Línea de Impuesto:" required>
                <b-form-select v-model="ruleForm.tax_catalog_line_id" :options="taxLineOptions" :disabled="!ruleForm.tax_catalog_id" required>
                  <template #first>
                    <b-form-select-option :value="null" disabled>Selecciona una línea</b-form-select-option>
                  </template>
                </b-form-select>
              </b-form-group>
            </b-col>
          </b-row>
        </b-card>

        <!-- Rule Properties -->
        <b-card class="mb-3">
          <template #header>
            <h6 class="mb-0">Propiedades de la Regla</h6>
          </template>
          
          <b-row>
            <b-col md="3">
              <b-form-group label="Prioridad:" description="1 = Mayor prioridad">
                <b-form-input v-model.number="ruleForm.priority" type="number" min="1" max="10" required></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group>
                <b-form-checkbox v-model="ruleForm.is_default">Regla por defecto</b-form-checkbox>
                <b-form-text>Se aplicará automáticamente a nuevos productos</b-form-text>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group>
                <b-form-checkbox v-model="ruleForm.is_active">Activo</b-form-checkbox>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group>
                <b-form-checkbox v-model="ruleForm.is_automatic">Aplicación Automática</b-form-checkbox>
                <b-form-text>Se aplica automáticamente en compras</b-form-text>
              </b-form-group>
            </b-col>
          </b-row>
          
          <b-row>
            <b-col md="6">
              <b-form-group label="Vigente desde:">
                <b-form-input v-model="ruleForm.effective_from" type="date"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Vigente hasta:">
                <b-form-input v-model="ruleForm.effective_to" type="date"></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>
        </b-card>

        <!-- Rule Conditions (Advanced) -->
        <b-card v-if="showAdvanced">
          <template #header>
            <h6 class="mb-0">Condiciones Avanzadas (Opcional)</h6>
          </template>
          
          <b-row>
            <b-col md="4">
              <b-form-group label="Monto mínimo:">
                <b-input-group>
                  <b-form-input v-model.number="ruleForm.min_amount" type="number" step="0.01" min="0"></b-form-input>
                  <b-input-group-append>
                    <b-input-group-text>$</b-input-group-text>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="Cantidad mínima:">
                <b-form-input v-model.number="ruleForm.min_quantity" type="number" step="0.01" min="0"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="Proveedor específico:">
                <b-form-select v-model="ruleForm.specific_supplier_id" :options="supplierOptions">
                  <template #first>
                    <b-form-select-option :value="null">Todos los proveedores</b-form-select-option>
                  </template>
                </b-form-select>
              </b-form-group>
            </b-col>
          </b-row>
        </b-card>
        
        <div class="text-center mt-3">
          <b-button variant="link" size="sm" @click="showAdvanced = !showAdvanced">
            {{ showAdvanced ? 'Ocultar' : 'Mostrar' }} opciones avanzadas
          </b-button>
        </div>
      </b-form>
      
      <template #modal-footer="{ hide }">
        <b-button variant="secondary" @click="hide">Cancelar</b-button>
        <b-button variant="primary" @click="saveRule" :disabled="!isValidRule">
          {{ ruleForm.id ? 'Actualizar' : 'Crear' }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'TaxRuleEditor',
  data() {
    return {
      taxRules: [],
      supplies: [],
      supplyCategories: [],
      taxCatalogs: [],
      suppliers: [],
      showRuleModal: false,
      showAdvanced: false,
      ruleScope: 'category',
      
      filters: {
        supply_category_id: null,
        supply_id: null,
        tax_code: null,
        is_active: null
      },
      
      ruleForm: {
        id: null,
        supply_category_id: null,
        supply_id: null,
        tax_catalog_id: null,
        tax_catalog_line_id: null,
        priority: 1,
        is_default: false,
        is_active: true,
        is_automatic: true,
        effective_from: null,
        effective_to: null,
        min_amount: null,
        min_quantity: null,
        specific_supplier_id: null
      },
      
      ruleFields: [
        { key: 'scope', label: 'Alcance', sortable: false },
        { key: 'tax_info', label: 'Impuesto', sortable: false },
        { key: 'priority', label: 'Prioridad', sortable: true },
        { key: 'effective_dates', label: 'Vigencia', sortable: false },
        { key: 'flags', label: 'Estado', sortable: false },
        { key: 'actions', label: 'Acciones' }
      ],
      
      scopeOptions: [
        { text: 'Aplicar a toda una categoría de productos', value: 'category' },
        { text: 'Aplicar a un producto específico', value: 'product' }
      ]
    };
  },
  
  computed: {
    taxCatalogOptions() {
      return this.taxCatalogs.map(catalog => ({
        value: catalog.id,
        text: `${catalog.name} (${catalog.tax_code})`
      }));
    },
    
    taxLineOptions() {
      if (!this.ruleForm.tax_catalog_id) return [];
      const selectedCatalog = this.taxCatalogs.find(c => c.id === this.ruleForm.tax_catalog_id);
      if (!selectedCatalog || !selectedCatalog.tax_lines) return [];
      return selectedCatalog.tax_lines.map(line => ({
        value: line.id,
        text: `${line.rate_name} - ${line.rate_value}%`
      }));
    },
    
    supplyCategoryOptions() {
      return this.supplyCategories.map(category => ({
        value: category.id,
        text: category.name
      }));
    },
    
    supplyOptions() {
      return this.supplies.map(supply => ({
        value: supply.id,
        text: `${supply.code} - ${supply.name}`
      }));
    },
    
    supplierOptions() {
      return this.suppliers.map(supplier => ({
        value: supplier.id,
        text: supplier.name
      }));
    },
    
    taxCodeFilterOptions() {
      const uniqueCodes = [...new Set(this.taxCatalogs.map(c => c.tax_code))];
      return uniqueCodes.map(code => ({
        value: code,
        text: code
      }));
    },
    
    isValidRule() {
      const hasScope = this.ruleForm.supply_category_id || this.ruleForm.supply_id;
      const hasTax = this.ruleForm.tax_catalog_id && this.ruleForm.tax_catalog_line_id;
      return hasScope && hasTax;
    }
  },
  
  mounted() {
    this.loadInitialData();
  },
  
  methods: {
    async loadInitialData() {
      await Promise.all([
        this.loadTaxRules(),
        this.loadSupplies(),
        this.loadSupplyCategories(),
        this.loadTaxCatalogs(),
        this.loadSuppliers()
      ]);
    },
    
    loadTaxRules() {
      let that = this;
      const params = new URLSearchParams();
      
      if (this.filters.supply_category_id) params.append('supply_category_id', this.filters.supply_category_id);
      if (this.filters.supply_id) params.append('supply_id', this.filters.supply_id);
      if (this.filters.tax_code) params.append('tax_code', this.filters.tax_code);
      if (this.filters.is_active !== null) params.append('is_active', this.filters.is_active);
      
      const queryString = params.toString();
      const url = queryString ? `/supply-tax-rules?${queryString}` : '/supply-tax-rules';
      
      axios
        .get(url)
        .then(function (response) {
          that.taxRules = response.data;
        })
        .catch(function (err) {
          that.makeToast('Error', err.response?.data || 'Error al cargar reglas de impuestos', 'danger');
        });
    },
    
    loadSupplies() {
      let that = this;
      return axios
        .get('/supplies')
        .then(function (response) {
          that.supplies = response.data.map(supply => ({
            ...supply,
            name: `${supply.code} - ${supply.name}`
          }));
        })
        .catch(function (err) {
          that.makeToast('Error', err.response?.data || 'Error al cargar productos', 'danger');
        });
    },
    
    loadSupplyCategories() {
      let that = this;
      return axios
        .get('/supplycategories')
        .then(function (response) {
          that.supplyCategories = response.data;
        })
        .catch(function (err) {
          that.makeToast('Error', err.response?.data || 'Error al cargar categorías', 'danger');
        });
    },
    
    loadTaxCatalogs() {
      let that = this;
      return axios
        .get('/tax-catalogs')
        .then(async function (response) {
          that.taxCatalogs = response.data;
          // Load tax lines for each catalog
          for (let catalog of that.taxCatalogs) {
            try {
              const linesResponse = await axios.get(`/tax-catalogs/${catalog.id}`);
              catalog.tax_lines = linesResponse.data.tax_lines || [];
            } catch (err) {
              console.error(`Error loading tax lines for catalog ${catalog.id}:`, err);
              catalog.tax_lines = [];
            }
          }
        })
        .catch(function (err) {
          that.makeToast('Error', err.response?.data || 'Error al cargar catálogos de impuestos', 'danger');
        });
    },
    
    loadSuppliers() {
      let that = this;
      return axios
        .get('/providers')
        .then(function (response) {
          that.suppliers = response.data;
        })
        .catch(function (err) {
          that.makeToast('Error', err.response?.data || 'Error al cargar proveedores', 'danger');
        });
    },
    
    showCreateModal() {
      this.resetRuleForm();
      this.ruleScope = 'category';
      this.showAdvanced = false;
      this.showRuleModal = true;
    },
    
    editRule(rule) {
      this.ruleForm = {
        id: rule.id,
        supply_category_id: rule.supply_category_id,
        supply_id: rule.supply_id,
        tax_catalog_id: rule.tax_catalog_id,
        tax_catalog_line_id: rule.tax_catalog_line_id,
        priority: rule.priority,
        is_default: rule.is_default,
        is_active: rule.is_active,
        is_automatic: rule.is_automatic || true,
        effective_from: rule.effective_from ? rule.effective_from.split('T')[0] : null,
        effective_to: rule.effective_to ? rule.effective_to.split('T')[0] : null,
        min_amount: rule.min_amount,
        min_quantity: rule.min_quantity,
        specific_supplier_id: rule.specific_supplier_id
      };
      
      this.ruleScope = rule.supply_id ? 'product' : 'category';
      this.showAdvanced = !!(rule.min_amount || rule.min_quantity || rule.specific_supplier_id);
      this.showRuleModal = true;
    },
    
    duplicateRule(rule) {
      this.editRule(rule);
      this.ruleForm.id = null;
      this.ruleForm.is_default = false;
      this.ruleForm.priority = rule.priority + 1;
    },
    
    saveRule() {
      if (!this.isValidRule) return;
      
      let that = this;
      const method = this.ruleForm.id ? 'put' : 'post';
      const url = this.ruleForm.id ? `/supply-tax-rules/${this.ruleForm.id}` : '/supply-tax-rules';
      
      // Clean the form data based on scope
      const formData = { ...this.ruleForm };
      if (this.ruleScope === 'category') {
        formData.supply_id = null;
      } else {
        formData.supply_category_id = null;
      }
      
      axios[method](url, formData)
        .then(function () {
          that.makeToast('Éxito', 'Regla guardada exitosamente', 'success');
          that.loadTaxRules();
          that.showRuleModal = false;
        })
        .catch(function (err) {
          that.makeToast('Error', err.response?.data || 'Error al guardar regla', 'danger');
        });
    },
    
    deleteRule(ruleId) {
      this.$bvModal.msgBoxConfirm('¿Estás seguro de que quieres eliminar esta regla? Esta acción no se puede deshacer.', {
        title: 'Confirmar eliminación',
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: 'Eliminar',
        cancelTitle: 'Cancelar',
        centered: true
      })
      .then(value => {
        if (value) {
          let that = this;
          axios
            .delete(`/supply-tax-rules/${ruleId}`)
            .then(function () {
              that.makeToast('Éxito', 'Regla eliminada exitosamente', 'success');
              that.loadTaxRules();
            })
            .catch(function (err) {
              that.makeToast('Error', err.response?.data || 'Error al eliminar regla', 'danger');
            });
        }
      });
    },
    
    onScopeChange() {
      if (this.ruleScope === 'category') {
        this.ruleForm.supply_id = null;
      } else {
        this.ruleForm.supply_category_id = null;
      }
    },
    
    onTaxCatalogChange() {
      this.ruleForm.tax_catalog_line_id = null;
    },
    
    resetRuleForm() {
      this.ruleForm = {
        id: null,
        supply_category_id: null,
        supply_id: null,
        tax_catalog_id: null,
        tax_catalog_line_id: null,
        priority: 1,
        is_default: false,
        is_active: true,
        is_automatic: true,
        effective_from: null,
        effective_to: null,
        min_amount: null,
        min_quantity: null,
        specific_supplier_id: null
      };
    },
    
    getTaxBadgeVariant(taxCode) {
      const variants = {
        'IVA': 'primary',
        'IEPS': 'warning',
        'ISR': 'info',
        'CEDULA': 'secondary',
        'ICA': 'success'
      };
      return variants[taxCode] || 'light';
    },
    
    getPriorityVariant(priority) {
      if (priority <= 2) return 'danger'; // High priority
      if (priority <= 5) return 'warning'; // Medium priority
      return 'success'; // Low priority
    },
    
    formatDate(dateString) {
      if (!dateString) return null;
      return new Date(dateString).toLocaleDateString('es-ES');
    },
    
    makeToast(title, message, variant) {
      this.$bvToast.toast(message, {
        title,
        variant,
        autoHideDelay: 5000,
        appendToast: true,
      });
    }
  }
};
</script>

<style scoped>
.table-sm td, .table-sm th {
  padding: 0.3rem;
}

.badge {
  margin-bottom: 2px;
}
</style>